import classNames from 'classnames'
import { X } from 'lucide-react'
import React, {
    ComponentType,
    DetailedHTMLProps,
    InputHTMLAttributes,
    MouseEvent,
    useEffect,
    useId,
    useRef,
    useState,
} from 'react'

import { FlexCol } from '../../../../common/frontend/components/flex-col/flex-col.js'
import { getMultiSelectProps, getSingleSelectProps } from '../../../props/base-select.js'
import type { IconProps } from '../../icon/icon.js'
import { Spinner } from '../../spinner/spinner.js'
import { Option } from '../../types/common.js'

export type InputSize = 'regular' | 'small'

export interface SelectNoteProps {
    text?: React.ReactNode
    isError?: boolean

    // Optional: supports the existing styleguide example
    isCheckbox?: boolean
    checkboxLabel?: React.ReactNode
    checkboxProps?: DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>
}

export interface SelectChipProps {
    label: string
    onRemove: () => void
}

export interface SelectOptionsProps {
    options: SelectOptionProps[]
}

export interface SelectOptionProps {
    isSelected?: boolean
    isHighlighted?: boolean
    scroll?: boolean
    label: string
    onMouseOver: () => void
    onMouseDown: (event: MouseEvent) => void
}

export interface BaseSelectProps {
    className?: string
    wrapperClass?: string
    label?: string | React.ReactNode
    chips?: SelectChipProps[]
    input?: DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>
    isLoading?: boolean
    icon?: ComponentType<IconProps>
    note?: SelectNoteProps
    options?: SelectOptionsProps
    isDisabled?: boolean
    size?: InputSize
    isRequired?: boolean
    // Mousedown on the trigger box (label/input/chevron). Lets the field toggle
    // closed when already open instead of the label re-focusing the input.
    onTriggerMouseDown?: (event: MouseEvent) => void
}

export interface SelectProps<T extends string = string> extends CommonSelectProps {
    className?: string
    inputClass?: string
    options: Option<T>[]
    value: T
    onChange: (value: T) => void
    placeholder?: string
    isDisabled?: boolean
    size?: InputSize
}

export interface MultiSelectProps<T extends string = string> extends CommonSelectProps {
    className?: string
    inputClass?: string
    options: Option<T>[]
    value: T[]
    onChange: (value: T[]) => void
    placeholder?: string
    isDisabled?: boolean
    size?: InputSize
}

export interface CommonSelectProps {
    wrapperClass?: string
    label?: string
    isRequired?: boolean
    isLoading?: boolean
    note?: SelectNoteProps
}

export const Select = <T extends string>(props: SelectProps<T>) => {
    const inputRef = useRef<HTMLInputElement>(null)
    const [search, setSearch] = useState('')
    const [isOpen, setOpen] = useState(false)
    const [highlightedIndex, setHighlightedIndex] = useState<number>(-1)

    const baseProps = getSingleSelectProps({
        ...props,
        inputRef,
        search,
        setSearch,
        isOpen,
        setOpen,
        highlightedIndex,
        setHighlightedIndex,
    })

    return <BaseSelect {...baseProps} />
}

export const MultiSelect = <T extends string>(props: MultiSelectProps<T>) => {
    const inputRef = useRef<HTMLInputElement>(null)
    const [search, setSearch] = useState('')
    const [isOpen, setOpen] = useState(false)
    const [highlightedIndex, setHighlightedIndex] = useState<number>(-1)

    const baseProps = getMultiSelectProps({
        ...props,
        inputRef,
        search,
        setSearch,
        isOpen,
        setOpen,
        highlightedIndex,
        setHighlightedIndex,
    })

    return <BaseSelect {...baseProps} />
}

const BaseSelect = (props: BaseSelectProps) => {
    // Associate the label with the input by id instead of wrapping the field in
    // a <label>. A wrapping label turns the whole container (the empty row beside
    // the label text, the gaps) into a click target that refocuses the input and
    // reopens the dropdown — making the popover near-impossible to close.
    const generatedId = useId()
    const controlId = props.input?.id ?? generatedId

    return (
        <FlexCol className={classNames('text-field', props.className)} gap={0.5}>
            <div className="select__root">
                <div className={classNames('select__container', 'text-field__container')}>
                    {props.label && (
                        <label
                            className={classNames('select__label', 'text-field__label')}
                            htmlFor={controlId}
                        >
                            {props.label}
                            {props.isRequired && (
                                <span className="text-field__required-asterisk"> *</span>
                            )}
                        </label>
                    )}
                    <span
                        className={classNames(
                            'select__input-wrapper',
                            'text-field__input-wrapper',
                            props.size && `select__input-wrapper--${props.size}`,
                            props.size && `text-field__input-wrapper--${props.size}`,
                            props.isDisabled && 'select__input-wrapper--disabled',
                            props.isDisabled && 'text-field__input-wrapper--disabled',
                            props.wrapperClass,
                        )}
                        onMouseDown={props.onTriggerMouseDown}
                    >
                        {props.chips?.map((chip, index) => (
                            <SelectChip key={index} {...chip} />
                        ))}
                        <input
                            {...props.input}
                            id={controlId}
                            className={classNames(props.input?.className, 'text-field__input')}
                        />
                        {props.isLoading && <Spinner size="small" />}
                        {props.icon && (
                            <props.icon
                                className={classNames(
                                    'select__icon',
                                    props.isDisabled && 'select__icon--disabled',
                                )}
                            />
                        )}
                    </span>
                </div>
                {props.options && <SelectOptions {...props.options} />}
            </div>
            {props.note && <SelectNote {...props.note} />}
        </FlexCol>
    )
}

const SelectChip = (props: SelectChipProps) => (
    <div className="select-chip">
        {props.label}
        <button
            type="button"
            onMouseDown={(event) => {
                if (event.button === 0) {
                    props.onRemove()
                }
            }}
            className="select-chip__remove"
        >
            <X />
        </button>
    </div>
)

const SelectOptions = (props: SelectOptionsProps) => (
    <div className="select-options">
        {props.options.map((option, index) => (
            <SelectOption key={index} {...option} />
        ))}
    </div>
)

const SelectOption = (props: SelectOptionProps) => {
    const ref = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (props.scroll) {
            ref.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
            })
        }
    }, [props.scroll])

    return (
        <div
            ref={ref}
            className={classNames(
                'select-options__option',
                props.isSelected && 'select-options__option--selected',
                props.isHighlighted && 'select-options__option--highlighted',
            )}
            onMouseOver={props.onMouseOver}
            onMouseDown={props.onMouseDown}
        >
            {props.label}
        </div>
    )
}

const SelectNote = (props: SelectNoteProps) => (
    <div className={classNames('select__note', props.isError && 'select__note--error')}>
        {props.isCheckbox && props.checkboxLabel && (
            <label className="select__note-checkbox">
                <input type="checkbox" {...props.checkboxProps} />
                <span className="select__note-checkbox-label">{props.checkboxLabel}</span>
            </label>
        )}
        {props.text && <div className="select__note-text">{props.text}</div>}
    </div>
)
